'use client';

// ✨ 1. Импортируем 'useCallback'
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useState } from 'react';

interface WeatherData {
  date: string;
  maxTemp: number;
  minTemp: number;
}

export default function HistoricalWeather() {
  // ✨ 2. ИЗМЕНЕНИЕ: Устанавливаем пустые значения по умолчанию
  const [date, setDate] = useState('');
  const [city, setCity] = useState('');

  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ✨ 3. УЛУЧШЕНИЕ: Получаем сегодняшнюю дату для 'max'
  // (чтобы нельзя было выбрать дату в будущем)
  const today = new Date().toISOString().split('T')[0];

  const normalizeCity = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

  const getTodayKey = () => new Date().toISOString().split('T')[0];

  const getDailyLimitState = () => {
    if (typeof window === 'undefined') return { date: getTodayKey(), count: 0 };
    try {
      const raw = window.localStorage.getItem('historicalWeatherDailyLimit');
      if (!raw) return { date: getTodayKey(), count: 0 };
      const parsed = JSON.parse(raw) as { date?: string; count?: number };
      if (parsed.date === getTodayKey() && typeof parsed.count === 'number') {
        return { date: parsed.date, count: parsed.count };
      }
    } catch (err) {
      console.warn('Не удалось прочитать лимит запросов:', err);
    }
    return { date: getTodayKey(), count: 0 };
  };

  const updateDailyLimitState = (nextCount: number) => {
    if (typeof window === 'undefined') return;
    const payload = { date: getTodayKey(), count: nextCount };
    try {
      window.localStorage.setItem('historicalWeatherDailyLimit', JSON.stringify(payload));
    } catch (err) {
      console.warn('Не удалось сохранить лимит запросов:', err);
    }
  };

  const getCacheKey = (normalizedCity: string, selectedDate: string) =>
    `historicalWeather:${normalizedCity}:${selectedDate}`;

  const handleSearch = useCallback(async () => {
    setWeather(null);
    setError(null);
    const normalizedCity = normalizeCity(city);
    const selectedDate = date.trim();

    if (!normalizedCity || normalizedCity.length < 2 || !selectedDate) {
      setError('Укажите корректный город и дату.');
      return;
    }

    const cacheKey = getCacheKey(normalizedCity, selectedDate);
    if (typeof window !== 'undefined') {
      try {
        const cached = window.localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached) as { data?: WeatherData; expiresAt?: number };
          if (parsed?.data && (!parsed.expiresAt || parsed.expiresAt > Date.now())) {
            setWeather(parsed.data);
            return;
          }
        }
      } catch (err) {
        console.warn('Не удалось прочитать кэш погоды:', err);
      }
    }

    const limitState = getDailyLimitState();
    if (limitState.count >= 5) {
      setError('Лимит 5 запросов в сутки исчерпан. Попробуйте завтра.');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(
        `/api/history?city=${encodeURIComponent(normalizedCity)}&date=${selectedDate}`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Ошибка');

      setWeather(data);
      updateDailyLimitState(limitState.count + 1);
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(
            cacheKey,
            JSON.stringify({
              data,
              expiresAt: Date.now() + 24 * 60 * 60 * 1000,
            }),
          );
        } catch (err) {
          console.warn('Не удалось сохранить кэш погоды:', err);
        }
      }
    } catch (err: unknown) {
      // ИСПРАВЛЕНО: Заменили any на unknown и добавили проверку типа
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Произошла ошибка');
      }
    } finally {
      setLoading(false);
    }
  }, [city, date]);

  return (
    <div
      className="w-full mx-auto p-4 bg-sky-900/40 rounded-xl text-white shadow-lg dark:bg-gradient-to-br dark:from-slate-800/50 dark:via-slate-700/60 dark:to-slate-800/50 dark:backdrop-blur-sm dark:border dark:border-slate-700 dark:text-slate-200"
      aria-busy={loading}
    >
      <h2 className="text-2xl font-bold mb-4 text-center text-main">
        Какая была погода в день твоего рождения?
      </h2>

      <form
        className="flex flex-col gap-3 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <label htmlFor="city-input" className="sr-only">
          Город
        </label>
        <input
          id="city-input"
          type="text"
          placeholder="Введите город (напр. Томск)" // 👈 Плейсхолдер теперь виден
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="px-4 py-2 rounded-xl text-white border border-gray-400 bg-slate-800/50 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
          required
        />

        <label htmlFor="date-input" className="sr-only">
          Дата
        </label>
        {/* ✨ 4. ИЗМЕНЕНИЕ: Возвращаем type="date" */}
        <input
          id="date-input"
          type="date" // 👈 Это вернет иконку календаря и формат (дд.мм.гггг)
          value={date} // 👈 Так как value='', поле будет пустым
          onChange={(e) => setDate(e.target.value)}
          max={today} // 👈 Нельзя выбрать завтра
          min="1940-01-01" // 👈 Ограничение API
          className="px-4 py-2 rounded-xl text-white border border-gray-400 bg-slate-800/50 [color-scheme:dark] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="bg-sky-600 hover:bg-sky-500 dark:bg-sky-500 dark:hover:bg-sky-600 text-xl rounded-xl p-2 mt-2 font-semibold cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Ищем...' : 'Узнать погоду'}
        </button>
      </form>

      <div className="min-h-[120px]" aria-live="polite">
        <AnimatePresence>
          {error && (
            <motion.p
              key="error"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-red-300 text-sm text-center"
            >
              {error}
            </motion.p>
          )}

          {weather && (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.4 }}
              className="text-center mt-4"
            >
              <motion.p
                className="text-xl mb-1 font-semibold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.1 }}
              >
                {city}, {weather.date}
              </motion.p>
              <motion.p
                className="text-2xl font-bold text-orange-200"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                🌡️ Макс: {weather.maxTemp}°C
              </motion.p>
              <motion.p
                className="text-xl text-blue-200 mt-1"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                ❄️ Мин: {weather.minTemp}°C
              </motion.p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
